/**
 * Sitemap Generator API
 * Provides interface to interact with the sitemap generation backend
 */
import { api } from "aldoo-ra/API";

// Construct the endpoint
const sitemapAPI = api("sitemap");

/**
 * Get sitemap entries with pagination and filtering
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Response with data, totalItems, totalPages
 */
export const GetSitemapEntries = async (options = {}) => {
    return await sitemapAPI({ get_entries: options });
};

/**
 * Add or update a sitemap entry
 * @param {Object} payload - The sitemap entry data
 * @returns {Promise<Object>} Response with result
 */
export const AddSitemapEntry = async (payload) => {
    // Note: We pass payload directly to match server-side expectations
    return await sitemapAPI({ add_entry: { payload } });
};

/**
 * Delete sitemap entries
 * @param {Array|string} ids - IDs of entries to delete
 * @param {string} mode - 'soft' or 'hard' delete
 * @returns {Promise<Object>} Response with result
 */
export const DeleteSitemapEntries = async (ids, mode = 'soft') => {
    return await sitemapAPI({ delete_entries: { ids, mode } });
};

/**
 * Restore deleted sitemap entries
 * @param {Array|string} ids - IDs of entries to restore
 * @returns {Promise<Object>} Response with result
 */
export const RestoreSitemapEntries = async (ids) => {
    return await sitemapAPI({ restore_entries: { ids } });
};

/**
 * Manually trigger sitemap generation
 * @returns {Promise<Object>} Response with result
 */
export const GenerateSitemap = async () => {
    return await sitemapAPI({ generate: {} });
};

/**
 * Get the list of registered data providers
 * @returns {Promise<Object>} Response with providers array
 */
export const GetSitemapProviders = async () => {
    return await sitemapAPI({ get_providers: {} });
};

/**
 * Get deleted sitemap entries for restoration
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Response with data, totalItems, totalPages
 */
export const GetDeletedSitemapEntries = async (options = {}) => {
    return await sitemapAPI({ get_deleted: options });
};

/**
 * Register a data provider (admin only)
 * Note: This is typically called programmatically rather than via API
 * @param {string} providerName - Name of the provider
 * @param {Function} providerFn - Provider function
 * @returns {Promise<Object>} Response with result
 */
export const RegisterSitemapProvider = async (providerName, providerFn) => {
    return await sitemapAPI({
        register_provider: { providerName, providerFn }
    });
};

// Export the sitemap API
export default {
    GetSitemapEntries,
    AddSitemapEntry,
    DeleteSitemapEntries,
    RestoreSitemapEntries,
    GenerateSitemap,
    GetSitemapProviders,
    GetDeletedSitemapEntries,
    RegisterSitemapProvider
};